import { GitBranch, Pencil, Plus, Sparkles, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useQueryClient } from "@tanstack/react-query";

import {
  useAdminEntries,
  useDeleteEntry,
  usePublishEntry,
  useUnpublishEntry,
  type Category,
  type Entry,
  type Status,
} from "../api";
import { EntryForm } from "../components/admin/EntryForm";
import { GitImportPanel } from "../components/admin/GitImportPanel";
import { CategoryBadge } from "../components/CategoryBadge";

const CATEGORY_TABS: { key: Category | "all"; label: string }[] = [
  { key: "all", label: "All" },
  { key: "new", label: "New" },
  { key: "enhancement", label: "Enhancements" },
  { key: "fix", label: "Fixes" },
];

const STATUS_FILTERS: { key: Status | "all"; label: string }[] = [
  { key: "all", label: "All statuses" },
  { key: "draft", label: "Draft" },
  { key: "published", label: "Published" },
];

export function WhatsNewAdminPage() {
  const [category, setCategory] = useState<Category | "all">("all");
  const [status, setStatus] = useState<Status | "all">("all");
  const [project, setProject] = useState<string | null>(null);
  const [showImport, setShowImport] = useState(false);
  const [editing, setEditing] = useState<Entry | null>(null);
  const [creating, setCreating] = useState(false);

  const qc = useQueryClient();

  const params = useMemo(
    () => ({
      category: category === "all" ? undefined : category,
      status: status === "all" ? undefined : status,
      project: project ?? undefined,
      limit: 200,
    }),
    [category, status, project],
  );

  const entries = useAdminEntries(params);
  const publish = usePublishEntry();
  const unpublish = useUnpublishEntry();
  const remove = useDeleteEntry();

  const projects = useMemo(() => {
    const all = new Set<string>();
    entries.data?.items.forEach((e) => all.add(e.project));
    return Array.from(all);
  }, [entries.data]);

  const handleDelete = async (entry: Entry) => {
    if (!window.confirm(`Delete "${entry.title}"? This cannot be undone.`)) return;
    await remove.mutateAsync(entry.id);
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
            <Sparkles className="h-5 w-5 text-brand" /> What&apos;s New
          </h1>
          <p className="text-sm text-muted-foreground">
            Manage release-note entries shown to users. Drafts are invisible
            until published.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={showImport ? "secondary" : "outline"}
            onClick={() => setShowImport((s) => !s)}
          >
            <GitBranch className="h-4 w-4" />
            Git import
          </Button>
          <Button onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" />
            New entry
          </Button>
        </div>
      </div>

      {showImport && (
        <GitImportPanel
          onDone={() =>
            qc.invalidateQueries({ queryKey: ["whats_new", "admin"] })
          }
        />
      )}

      <Card>
        <CardContent className="p-0">
          <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3">
            <div className="flex flex-wrap gap-1">
              {CATEGORY_TABS.map((t) => (
                <Pill
                  key={t.key}
                  active={category === t.key}
                  onClick={() => setCategory(t.key)}
                >
                  {t.label}
                </Pill>
              ))}
            </div>
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as Status | "all")}
                className="h-8 rounded-md border border-input bg-transparent px-2 text-xs"
              >
                {STATUS_FILTERS.map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.label}
                  </option>
                ))}
              </select>
              {projects.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  <Pill
                    active={project === null}
                    onClick={() => setProject(null)}
                  >
                    All projects
                  </Pill>
                  {projects.map((p) => (
                    <Pill
                      key={p}
                      active={project === p}
                      onClick={() => setProject(p)}
                    >
                      {p}
                    </Pill>
                  ))}
                </div>
              )}
            </div>
          </div>

          <Table containerClassName="max-h-[calc(100vh-16rem)]">
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead className="w-32">Category</TableHead>
                <TableHead className="w-28">Project</TableHead>
                <TableHead className="w-24">Vis.</TableHead>
                <TableHead className="w-24">Status</TableHead>
                <TableHead className="w-32">Release</TableHead>
                <TableHead className="w-48 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.isLoading && (
                <TableRow>
                  <TableCell colSpan={7} className="py-10 text-center text-sm text-muted-foreground">
                    Loading…
                  </TableCell>
                </TableRow>
              )}
              {!entries.isLoading && entries.data?.items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="py-10 text-center text-sm text-muted-foreground">
                    No entries yet. Use <strong>Git import</strong> to seed from your commit history, or click <strong>New entry</strong> to author one.
                  </TableCell>
                </TableRow>
              )}
              {entries.data?.items.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-medium leading-tight">{entry.title}</span>
                      {entry.build_number && (
                        <span className="font-mono text-[10px] text-muted-foreground">
                          {entry.build_number}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <CategoryBadge category={entry.category} />
                  </TableCell>
                  <TableCell className="text-xs">{entry.project}</TableCell>
                  <TableCell className="text-xs">
                    {entry.visibility === "permission" ? (
                      // Show the gate, not the word "permission" — which gate it
                      // is, is the whole content of this cell.
                      <span
                        className="font-mono text-[11px]"
                        title={`Visible to holders of ${entry.required_permission}`}
                      >
                        {entry.required_permission}
                      </span>
                    ) : (
                      entry.visibility
                    )}
                  </TableCell>
                  <TableCell>
                    <span
                      className={
                        "inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase " +
                        (entry.status === "published"
                          ? "bg-success-light text-success"
                          : "bg-muted text-muted-foreground")
                      }
                    >
                      {entry.status}
                    </span>
                  </TableCell>
                  <TableCell className="text-xs">{entry.release_date}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditing(entry)}
                      >
                        <Pencil className="h-3 w-3" />
                        Edit
                      </Button>
                      {entry.status === "draft" ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => publish.mutate(entry.id)}
                          disabled={publish.isPending}
                        >
                          Publish
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => unpublish.mutate(entry.id)}
                          disabled={unpublish.isPending}
                        >
                          Unpublish
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(entry)}
                        disabled={remove.isPending}
                      >
                        <Trash2 className="h-3 w-3 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <EntryForm
        initial={editing}
        open={!!editing}
        onClose={() => setEditing(null)}
      />
      <EntryForm
        initial={null}
        open={creating}
        onClose={() => setCreating(false)}
      />
    </div>
  );
}

function Pill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors " +
        (active
          ? "border-brand bg-brand text-white"
          : "border-border bg-background text-foreground/80 hover:bg-accent")
      }
    >
      {children}
    </button>
  );
}
